import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import { getEnv } from '@risksmart-app/components/src/utils/environment';
import { useCallback } from 'react';

import type {
  AISuggestedRiskControlsResult,
  AIWorkflowJobResult,
} from '@/components/side-panel/implementations/ai/assistance/suggested-risk-controls/useAISuggestedRiskControlsStore';
import { useAISuggestedRiskControlsStore } from '@/components/side-panel/implementations/ai/assistance/suggested-risk-controls/useAISuggestedRiskControlsStore';
import { handleError } from '@/utils/errorUtils';

async function checkJob(
  token: string,
  aiWorkflowApiUrl: string,
  location: string
): Promise<AIWorkflowJobResult<AISuggestedRiskControlsResult>> {
  const result = await fetch(`${aiWorkflowApiUrl}${location}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!result.ok) {
    throw new Error(
      `Something went wrong trying to get the current status of the workflow job. Error: ${await result.text()}`
    );
  }

  return await result.json();
}

async function suggestControlsUsingAi(
  aiWorkflowApiUrl: string,
  token: string,
  riskId: string
): Promise<Response> {
  const result = await fetch(
    `${aiWorkflowApiUrl}/ai-engine/workflow-api/workflows/suggest-controls-by-risk-id`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        risk_id: riskId,
        exclude_existing_controls: true,
        use_library_controls: true,
        library_match_threshold: 0.85,
        additional_context: null,
      }),
    }
  );

  if (!result.ok) {
    throw new Error(
      `Encountered an error whilst trying to obtain risk control suggestions. Error: ${await result.text()}`
    );
  }

  return result;
}

export const useAiSuggestedRiskControlsService = (
  retryInterval: number = 3_000
) => {
  const { reset, jobStarted, setResults, setHasErrored, setHasTimedOut } =
    useAISuggestedRiskControlsStore();
  const { getAccessTokenSilently, isAuthenticated } = useRisksmartUser();

  function getWorkflowApiUrl(): string {
    const aiWorkflowApiUrl = getEnv('REACT_APP_AI_WORKFLOW_API_URL');

    if (!aiWorkflowApiUrl) {
      throw new Error('Unable to find the URL for the workflow API');
    }

    return aiWorkflowApiUrl;
  }

  const suggestRiskControls = useCallback(
    async (riskId: string) => {
      async function getAccessToken(): Promise<string> {
        if (!isAuthenticated) {
          throw new Error(
            'Attempting to get suggested risk controls, but the user is not authenticated'
          );
        }

        return await getAccessTokenSilently();
      }

      function pollForResults(
        aiWorkflowApiUrl: string,
        job: AIWorkflowJobResult<AISuggestedRiskControlsResult>
      ): void {
        let retries = 0;

        const intervalId = setInterval(async () => {
          try {
            const result = await checkJob(
              await getAccessTokenSilently(),
              aiWorkflowApiUrl,
              job.location
            );

            if (result.status === 'completed') {
              clearInterval(intervalId);

              setResults(result);
            } else if (result.status === 'failed') {
              // noinspection ExceptionCaughtLocallyJS
              throw new Error(
                result.error ??
                  `The suggested controls workflow job (${job.jobId}) has failed but has not given a reason why`
              );
            } else {
              retries += 1;
            }

            if (retries > 10) {
              clearInterval(intervalId);

              setHasTimedOut();
            }
          } catch (e) {
            clearInterval(intervalId);

            setHasErrored();

            handleError(e);
          }
        }, retryInterval);
      }

      try {
        const token = await getAccessToken();

        const aiWorkflowApiUrl = getWorkflowApiUrl();

        reset();

        const result = await suggestControlsUsingAi(
          aiWorkflowApiUrl,
          token,
          riskId
        );

        const response =
          (await result.json()) as AIWorkflowJobResult<AISuggestedRiskControlsResult>;

        if (response.status !== 'completed') {
          jobStarted(riskId, response.jobId);
        }

        pollForResults(aiWorkflowApiUrl, response);
      } catch (e) {
        setHasErrored();

        handleError(e);
      }
    },
    [
      getAccessTokenSilently,
      setHasErrored,
      setHasTimedOut,
      reset,
      jobStarted,
      setResults,
      isAuthenticated,
      retryInterval,
    ]
  );

  return {
    suggestRiskControls,
  };
};
